// =============================================================================
// SIREPPAR - Textos precargados de los informes de REEMPADRONAMIENTO
// =============================================================================
// La supervisora eleva un informe al Secretario Electoral y ES EL SECRETARIO QUIEN
// RESUELVE cuando el expediente está correcto: aprueba y otorga el reconocimiento
// con su propia resolución. Solo los expedientes que no están correctos se elevan
// al Tribunal, que aprueba, rechaza o aplaza.
//
// En los pasos de informe el cuerpo es editable y se le pueden adjuntar las
// intimaciones/observaciones del expediente desde el panel lateral.
//
// Los cuerpos de abajo son BREVES a propósito y no enumeran el expediente. Lo que
// hay que mostrar en concreto se adjunta tildándolo en el panel lateral, que arma
// el bloque de "Antecedentes" con el estado de cada observación
// (ver utils/historialInforme.ts).
//
//   EN_ANALISIS / OBSERVADO
//      └─ Informe al Secretario  → EN_SECRETARIO
//   EN_SECRETARIO
//      ├─ Aprobar (Secretario)   → expediente VIGENTE + partido VIGENTE
//      │     El Secretario resuelve por sí mismo cuando el expediente está
//      │     CORRECTO. Sube su resolución y otorga el reconocimiento.
//      └─ Elevar al TEP          → EN_TEP  (solo los casos que no están correctos)
//   EN_TEP
//      ├─ TEP aprueba            → expediente VIGENTE + partido VIGENTE
//      ├─ TEP rechaza            → expediente RECHAZADO (el partido no cambia)
//      └─ TEP aplaza             → expediente EN_ANALISIS
//
// Los textos de resolución de abajo son PRECARGADOS y EDITABLES: son el punto de
// partida del acto, no su redacción final, y son lo que se le manda al partido por
// correo cuando se tilda el aviso.
//
// A CONFIRMAR con la Secretaría antes de producción: la redacción de los cuatro
// textos resolutorios.
// =============================================================================

interface ExpedienteMin {
  numero?: string;
  partido?: { denominacion?: string } | null;
}

const denom = (exp: ExpedienteMin) => exp.partido?.denominacion || 'la agrupación';

/**
 * Sección de documentación del expediente, proyectada a la dimensión que
 * corresponde. El informe al Secretario ya NO la usa (ver abajo), pero el tipo se
 * mantiene acá porque ExpedienteDetailPage lo usa para el panel de requisitos.
 */
export interface SeccionInforme {
  tipo: string;
  obligatorio: boolean;
  admisibilidad: { estado: string };
  analisis: { estado: string };
}

/**
 * Cuerpo del informe con el que la SEP eleva el expediente al Secretario.
 *
 * Es DELIBERADAMENTE BREVE y NO enumera nada: ni tipos de documento, ni archivos,
 * ni cuántas observaciones quedaron subsanadas o sin subsanar.
 *
 * Antes armaba ese detalle a partir del estado de cada sección. El problema no era
 * que estuviera mal, sino que el informe terminaba siendo un volcado del expediente
 * —que el Secretario ya tiene delante, mejor presentado, en /documentos y en la tab
 * Observaciones— y enterraba lo único que el informe agrega: que el análisis está
 * cerrado y la decisión es suya.
 *
 * Lo puntual se adjunta a mano: al tildar intimaciones u observaciones en el panel
 * lateral se agrega el bloque de "Antecedentes" al final del cuerpo, y cada una
 * llega con su estado (subsanada / sin subsanar). Ver utils/historialInforme.ts.
 *
 * Sigue siendo editable en el modal: es un texto de la Secretaría, no un dictamen
 * del sistema.
 */
export function textoInformeSecretarioReemp(exp: ExpedienteMin): string {
  return (
    `Se informa al Señor Secretario Electoral que se ha completado el análisis del trámite de ` +
    `**reempadronamiento** de ${denom(exp)}, en el marco del expediente N° ${exp.numero || ''}.\n\n` +
    `Se analizó la totalidad de la documentación incorporada al expediente.\n\n` +
    `En virtud de lo expuesto, se eleva el expediente a su consideración, a fin de que resuelva ` +
    `cómo proseguir.`
  );
}

/**
 * Cuerpo del informe con el que el Secretario eleva el expediente al TEP.
 *
 * Es CORTO A PROPÓSITO y deja el hueco del medio para escribir. Desde que el
 * Secretario puede aprobar por sí mismo, al TEP solo se elevan los expedientes que
 * NO están correctos: el motivo de la elevación es justamente lo que hay que
 * redactar caso por caso, y el párrafo genérico que había antes afirmaba lo
 * contrario ("no existiendo objeciones que impidan la continuidad del trámite").
 */
export function textoInformeTepReemp(exp: ExpedienteMin): string {
  return (
    `Se eleva al Tribunal Electoral Provincial (TEP) el expediente N° ${exp.numero || ''}, ` +
    `correspondiente al trámite de **reempadronamiento** de ${denom(exp)}.\n\n` +
    `------------` +
    `[Detallar acá las cuestiones que motivan la elevación y sobre las que se solicita que el ` +
    `Tribunal resuelva.` +
    `----------------]\n\n` +
    `En virtud de lo expuesto, se remiten las actuaciones para su resolución conforme a derecho.`
  );
}

// =============================================================================
// Textos de los actos resolutorios (precargados y editables)
// =============================================================================
// Cada uno es lo que se le manda al partido por correo si se tilda el aviso al pie
// del modal, así que están escritos para leerse solos, sin el expediente delante.

/** El TEP aprueba: mismo efecto que la aprobación del Secretario. */
export function textoTepApruebaReemp(): string {
  return (
    'El Tribunal Electoral Provincial resuelve aprobar el reempadronamiento de la agrupación, por ' +
    'encontrarse cumplidos los requisitos exigidos por la Ley XII N° 23.'
  );
}

/**
 * El TEP rechaza. El expediente queda RECHAZADO; el estado de la agrupación NO
 * cambia, porque la caducidad es un acto distinto con su propio expediente.
 */
export function textoTepRechazaReemp(): string {
  return (
    'El Tribunal Electoral Provincial resuelve rechazar el reempadronamiento de la agrupación, ' +
    'conforme a los fundamentos que constan en el acta.\n\n'
  );
}

/**
 * El TEP aplaza. El expediente vuelve a análisis y el plazo NO arranca con el acta:
 * corre desde la intimación que la Secretaría curse con lo que el Tribunal
 * determinó. Decirlo en el texto evita que el partido cuente el plazo desde una
 * resolución que todavía no se le notificó en detalle.
 */
export function textoTepAplazaReemp(): string {
  return (
    'El Tribunal Electoral Provincial resuelve aplazar la resolución del reempadronamiento de la ' +
    'agrupación y otorgarle un plazo para subsanar lo señalado.\n\n' +
    'El plazo comenzará a correr una vez que la Secretaría Electoral le curse la intimación con lo ' +
    'que el Tribunal determinó.'
  );
}
